import { useQuery, useMutation } from '@tanstack/react-query';
import { getReports, viewReport } from '../../../services/reports';

/*
  1. getReports(listId) — list of { _id, name } report entries, rendered as cards.
  2. viewReport({ applicationId, reportId }) — fired on click of a card's
     View icon. Response is { data: { _id, name, reportData } } where
     reportData is a full HTML document string.

  On success, the HTML is:
   - opened in a new tab (Blob URL, text/html) so the user can preview it, and
   - downloaded as a .doc file (same HTML, served as application/msword —
     Word opens HTML content fine when saved with a .doc extension), named
     after the report's `name` field.

  Both are triggered from the same mutation so a single click satisfies
  "open in a new tab AND download into systems".
*/

export const useReports = (listId) => {
  const reportsQuery = useQuery({
    queryKey: ['reports', listId],
    queryFn: () => getReports(listId),
    enabled: !!listId,
  });

  const viewReportMutation = useMutation({
    mutationFn: (reportId) => viewReport({ applicationId: listId, reportId }),
    onSuccess: (response) => {
      const report = response?.data;
      const html = report?.reportData;
      if (!html) return;

      const fileName = report?.name || 'ASSESSMENT_REPORT';

      // Open in a new tab for preview
      const previewBlob = new Blob([html], { type: 'text/html' });
      const previewUrl = URL.createObjectURL(previewBlob);
      window.open(previewUrl, '_blank', 'noopener,noreferrer');

      // Download as a .doc file
      const docBlob = new Blob([html], { type: 'application/msword' });
      const docUrl = URL.createObjectURL(docBlob);
      const link = document.createElement('a');
      link.href = docUrl;
      link.download = `${fileName}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Release the object URLs once the browser has had a chance to use them
      setTimeout(() => {
        URL.revokeObjectURL(previewUrl);
        URL.revokeObjectURL(docUrl);
      }, 1000);
    },
  });

  const handleViewReport = (reportId) => viewReportMutation.mutate(reportId);

  return {
    reportsQuery,
    reports: reportsQuery.data?.data ?? [],
    handleViewReport,
    viewReportMutation,
  };
};
